import { supabase } from '../../lib/supabase';
import { teachClaimSoftNumber, teachAccountEdgeNumber, recheckJobsWithItem } from '../admin/Ordering/lineFix';

/*
  EDITING AN ITEM ON THE ITEM, job 37, 12 October 2026.

  **His words of 6 October**: once setup is done, *"I can just manually go into
  the item and makes the changes."* And of 12 October: *"yes build the on-item
  editing next."* Two halves:

  1. **The company's own fields**, his decision 6 of 2 October: billing, money,
     stock control, our description, notes. They live on `org_items`, one row
     per company per product, so editing them changes nothing for any other
     company. The vendor's own fields are shared by every company and are not
     edited here.
  2. **The ClaimSoft and Account Edge numbers**, under the four rules of 26
     September that the database enforces: one main number per system, an
     extra number only as a Medicare number, a number on one item only, and
     every one recorded as matched by hand, by whom and when. **After any change
     the open jobs are filled in and checked again**, the same as fixing a line
     on a job, by calling the same code.

  Every save is proved by the row coming back.
*/

/* The company fields, in his groups. `kind` says how each is typed. */
export const COMPANY_GROUPS = [
  { title: 'Billing', fields: [
    { key: 'hcpcs', label: 'HCPCS code', kind: 'text' },
    { key: 'mod_1', label: 'Modifier 1', kind: 'text' },
    { key: 'mod_2', label: 'Modifier 2', kind: 'text' },
    { key: 'mod_3', label: 'Modifier 3', kind: 'text' },
    { key: 'mod_4', label: 'Modifier 4', kind: 'text' },
    { key: 'ndc_number', label: 'NDC number', kind: 'text' },
    { key: 'is_billable', label: 'Billable', kind: 'tick' },
    { key: 'hcpcs_is_the_item', label: 'The HCPCS code is used as the item', kind: 'tick' },
  ] },
  { title: 'Money', fields: [
    { key: 'cost', label: 'Our cost each', kind: 'money', hint: 'Empty uses the newest price book' },
    { key: 'selling_price', label: 'Selling price', kind: 'money' },
    { key: 'rental_price', label: 'Rental price', kind: 'money' },
    { key: 'used_price', label: 'Used price', kind: 'money' },
  ] },
  { title: 'Stock control', fields: [
    { key: 'par_level', label: 'Par level', kind: 'number' },
    { key: 'min_order_qty', label: 'Minimum order quantity', kind: 'number' },
    { key: 'lead_time_days', label: 'Lead time, days', kind: 'number' },
    { key: 'is_stocked', label: 'We stock it', kind: 'tick' },
    { key: 'buy_from_this_vendor', label: 'Buy it from this vendor', kind: 'tick' },
    { key: 'is_serialized', label: 'Tracked by serial number', kind: 'tick' },
    { key: 'allow_negative_qty', label: 'Allow a negative quantity', kind: 'tick' },
  ] },
];
export const COMPANY_FIELDS = [
  { key: 'our_description', label: 'Our description', kind: 'text' },
  ...COMPANY_GROUPS.flatMap(g => g.fields),
  { key: 'notes', label: 'Notes', kind: 'text' },
];

/*
  **What a form becomes before it is saved**, pure so it can be tested. Text is
  trimmed and empty becomes nothing; a number must be a number and not below
  zero; a tick is true or false. Anything wrong is refused, naming the field,
  and nothing is saved.
*/
export function cleanCompanyFields(form) {
  const row = {};
  for (const f of COMPANY_FIELDS) {
    if (!(f.key in form)) continue;
    const v = form[f.key];
    if (f.kind === 'tick') { row[f.key] = !!v; continue; }
    const s = String(v ?? '').trim();
    if (f.kind === 'text') { row[f.key] = s === '' ? null : s; continue; }
    if (s === '') { row[f.key] = null; continue; }
    const n = Number(s.replace(/[$,]/g, ''));
    if (!Number.isFinite(n) || n < 0) return { row: null, problem: f.label + ' has to be a number, 0 or more.' };
    row[f.key] = n;
  }
  return { row, problem: null };
}

const refuse = message => ({ ok: false, error: { message } });

/* The company's own record of a product, made if this is the first time. */
export async function makeOurs(catalogItemId) {
  const { data: had } = await supabase.from('org_items').select('id').eq('catalog_item_id', catalogItemId).maybeSingle();
  if (had) return { ok: true, orgItemId: had.id };
  const { data, error } = await supabase.from('org_items').insert({ catalog_item_id: catalogItemId }).select('id').maybeSingle();
  if (error) return { ok: false, error };
  if (!data) return refuse('The item did not save.');
  return { ok: true, orgItemId: data.id };
}

export async function saveCompanyFields(orgItemId, form) {
  const { row, problem } = cleanCompanyFields(form);
  if (problem) return refuse(problem);
  const { data, error } = await supabase.from('org_items')
    .update({ ...row, updated_at: new Date().toISOString() }).eq('id', orgItemId).select('id').maybeSingle();
  if (error) return { ok: false, error };
  if (!data) return refuse('That did not save. Nothing was changed.');
  return { ok: true };
}

const SYSTEM = { claimsoft: 'ClaimSoft', account_edge: 'Account Edge' };

/* Which item a number is already on, in words, for a refusal. */
async function whereIs(system, key) {
  const { data, error } = await supabase.from('org_item_numbers')
    .select('id, org_item_id, org_item:org_items(catalog_item_id, item:catalog_items!org_items_catalog_item_id_fkey(oh_number, our_description, vendor_description))')
    .eq('system', system).eq('number_key', key).maybeSingle();
  if (error) return { error };
  if (!data) return { on: null };
  const it = data.org_item && data.org_item.item;
  return { on: { id: data.id, catalogItemId: data.org_item ? data.org_item.catalog_item_id : null,
    words: it ? [it.oh_number, it.our_description || it.vendor_description].filter(Boolean).join(', ') : 'another item' } };
}

/* After a number changes, do what fixing a job line does: fill in open lines
   carrying it and check the jobs again. */
async function afterNumberChange(system, number, catalogItemId, userId) {
  const r = system === 'claimsoft'
    ? await teachClaimSoftNumber({ number, catalogItemId, userId })
    : await teachAccountEdgeNumber({ aeNumber: number, catalogItemId, userId });
  return r.ok ? { nowReady: r.nowReady || [], lines: r.lines || 0, warning: r.warning } : { warning: 'The number is saved, but the open jobs could not be checked: ' + r.error.message };
}

/*
  **Add a number.** `medicare` is only for ClaimSoft: an extra number used for
  Medicare billing, never the main one. Refused, with nothing changed, if the
  number is already on any item, or if the item already has a main number for
  that system (then it is Change, not Add).
*/
export async function addNumber({ catalogItemId, system, number, medicare = false, userId }) {
  const n = String(number || '').trim();
  if (!n) return refuse('Type the number.');
  if (medicare && system !== 'claimsoft') return refuse('Only a ClaimSoft number can be a Medicare number.');
  const where = await whereIs(system, n.toUpperCase());
  if (where.error) return { ok: false, error: where.error };
  if (where.on) {
    return refuse(SYSTEM[system] + ' ' + n + (where.on.catalogItemId === catalogItemId
      ? ' is already on this item.' : ' is already on ' + where.on.words + ', so nothing was changed.'));
  }
  const ours = await makeOurs(catalogItemId);
  if (!ours.ok) return ours;
  if (!medicare) {
    const { data: main } = await supabase.from('org_item_numbers').select('number')
      .eq('org_item_id', ours.orgItemId).eq('system', system).eq('is_main', true).maybeSingle();
    if (main) return refuse('This item already has ' + SYSTEM[system] + ' ' + main.number + ' as its main number. Use Change to replace it.');
  }
  const { data, error } = await supabase.from('org_item_numbers').insert({
    org_item_id: ours.orgItemId, system, number: n, is_main: !medicare, reason: medicare ? 'medicare_billing' : null,
    match_method: 'manual', match_status: 'confirmed', confirmed_by: userId || null, confirmed_at: new Date().toISOString(),
  }).select('id').maybeSingle();
  if (error) return { ok: false, error };
  if (!data) return refuse('The number did not save. Nothing was changed.');
  return { ok: true, ...(await afterNumberChange(system, n, catalogItemId, userId)) };
}

/* **Change a number** to another, keeping whether it is the main or the
   Medicare one. Recorded as matched by hand, by whom and when. */
export async function changeNumber({ numberId, catalogItemId, system, number, userId }) {
  const n = String(number || '').trim();
  if (!n) return refuse('Type the new number.');
  const where = await whereIs(system, n.toUpperCase());
  if (where.error) return { ok: false, error: where.error };
  if (where.on && where.on.id !== numberId) {
    return refuse(SYSTEM[system] + ' ' + n + (where.on.catalogItemId === catalogItemId
      ? ' is already on this item.' : ' is already on ' + where.on.words + ', so nothing was changed.'));
  }
  const { data, error } = await supabase.from('org_item_numbers').update({
    number: n, match_method: 'manual', match_status: 'confirmed', confirmed_by: userId || null,
    confirmed_at: new Date().toISOString(), updated_at: new Date().toISOString(),
  }).eq('id', numberId).select('id').maybeSingle();
  if (error) return { ok: false, error };
  if (!data) return refuse('That did not save. Nothing was changed.');
  return { ok: true, ...(await afterNumberChange(system, n, catalogItemId, userId)) };
}

/* **Remove a number.** Open jobs with the item are checked again, because a
   job missing an Account Edge number is not Ready. */
export async function removeNumber({ numberId, catalogItemId }) {
  const { data, error } = await supabase.from('org_item_numbers').delete().eq('id', numberId).select('id').maybeSingle();
  if (error) return { ok: false, error };
  if (!data) return refuse('That was not removed. It may already have gone.');
  const back = await recheckJobsWithItem(catalogItemId);
  return { ok: true, backToNeedsYou: back };
}
